import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '../../components/layout/MainLayout';
import { Upload, CheckCircle, AlertTriangle, FileText, X } from 'lucide-react';
import {
  orderImportService,
  UploadResult,
  ValidationError,
  ValidationFailure,
} from '../../api/services/orderImportService';

type UploadState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'success'; result: UploadResult }
  | { status: 'validation'; failure: ValidationFailure }
  | { status: 'error'; message: string };

export default function OrderImport() {
  const [customerId, setCustomerId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: customers = [], isLoading: customersLoading } = useQuery({
    queryKey: ['edi-customers'],
    queryFn: orderImportService.getEdiCustomers,
    refetchOnWindowFocus: false,
  });

  const selectedCustomer = customers.find(c => c.CustomerID === customerId);

  const handleFileChange = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    setUploadState({ status: 'idle' });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.name.endsWith('.csv')) handleFileChange(dropped);
  };

  const handleUpload = async () => {
    if (!file || !customerId) return;
    setUploadState({ status: 'uploading' });
    try {
      const result = await orderImportService.uploadCSV(customerId, file);
      if ('isValidation' in result) {
        setUploadState({ status: 'validation', failure: result });
      } else {
        setUploadState({ status: 'success', result });
      }
    } catch (err) {
      setUploadState({ status: 'error', message: err instanceof Error ? err.message : 'Upload failed' });
    }
  };

  const reset = () => {
    setFile(null);
    setUploadState({ status: 'idle' });
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Order Import</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload an EDI order CSV for a customer configured for EDI Integration. Orders are imported into{' '}
            <span className="font-mono">Pending Orders</span> for review before confirmation.
          </p>
        </div>

        {/* Customer select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</label>
          <select
            value={customerId}
            onChange={(e) => { setCustomerId(e.target.value); setUploadState({ status: 'idle' }); }}
            disabled={customersLoading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50"
          >
            <option value="">
              {customersLoading ? 'Loading customers...' : 'Select a customer...'}
            </option>
            {customers.map((c) => (
              <option key={c.CustomerID} value={c.CustomerID}>
                {c.Name} ({c.CustomerID})
              </option>
            ))}
          </select>
          {!customersLoading && customers.length === 0 && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              No customers are configured for EDI Integration. Enable it under Admin &rarr; Customer Config.
            </p>
          )}
          {selectedCustomer && (
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={!!selectedCustomer.Dropship} readOnly className="h-4 w-4 rounded border-gray-300" />
              Drop Ship
            </label>
          )}
          {selectedCustomer?.Dropship && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This customer is configured for Drop Ship — a <span className="font-mono">DS-1</span> line item will
              be added automatically to each imported order.
            </p>
          )}
        </div>

        {/* Expected format */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Required CSV columns:</p>
          <code className="text-xs text-blue-700 dark:text-blue-400 break-all">
            Date, Ship By, Ship to Name, Ship to Address-Line One, Ship to Address-Line Two, Ship to City,
            Ship to State, Ship to Zipcode, Ship to Country, Customer PO, Ship Via, Invoice Note, Quantity,
            Item ID, Description, Unit Price
          </code>
          <p className="mt-2 text-xs text-blue-700 dark:text-blue-400">
            One row per line item. Rows sharing the same <span className="font-medium">Customer PO</span> are
            grouped into a single order. All orders are imported atomically — nothing is written if any row
            fails validation or a Customer PO already exists for this customer.
          </p>
        </div>

        {/* Drop zone */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            dragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={24} className="text-blue-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">{file.name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); reset(); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload size={32} className="mx-auto text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Drop a CSV file here, or <span className="text-blue-500 font-medium">click to browse</span>
              </p>
              <p className="text-xs text-gray-400">.csv only — max 10 MB</p>
            </div>
          )}
        </div>

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || !customerId || uploadState.status === 'uploading'}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
        >
          {uploadState.status === 'uploading' ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Upload size={16} />
              Import Orders
            </>
          )}
        </button>

        {/* Results */}
        {uploadState.status === 'success' && (
          <div className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-300">{uploadState.result.message}</p>
              <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
                Review imported orders under Orders &rarr; Pending Orders.
              </p>
            </div>
          </div>
        )}

        {uploadState.status === 'error' && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{uploadState.message}</p>
          </div>
        )}

        {uploadState.status === 'validation' && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <AlertTriangle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-300">{uploadState.failure.message}</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-0.5">
                  Fix the errors below and re-upload the file.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Row</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Column</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Issue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {uploadState.failure.errors.map((err: ValidationError, i: number) => (
                    <tr key={i} className="bg-white dark:bg-gray-900">
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400 font-mono">{err.row}</td>
                      <td className="px-4 py-2 text-gray-800 dark:text-gray-200 font-mono">{err.column}</td>
                      <td className="px-4 py-2 text-red-600 dark:text-red-400">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
